import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  // Tooltip,
  VisuallyHidden,
} from '@chakra-ui/react';
import { CheckIcon, CopyIcon } from '@chakra-ui/icons';
import { WalletState } from '@features/wallet/walletSlice';
import { useAuth, useCopyableRef } from '@hooks';
import { useChainManager } from '../../chain/hooks/useChainManager';
import { formatAddress } from '../../web3/utils/formatAddress';
import { getChainConfig } from '../../config/chains';
import React from 'react';

export interface WalletInfoProps {
  wallet: WalletState;
}

export const WalletInfo: React.FC<WalletInfoProps> = ({ wallet }) => {
  const { currentChain } = useChainManager();
  const { address, network, balance, symbol } = wallet;
  const { ref, isCopied, copy } = useCopyableRef<HTMLSpanElement>();
  const { signOut } = useAuth();
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipTimeout, setTooltipTimeout] =
    React.useState<NodeJS.Timeout | null>(null);

  const fallbackAddress = `0x${'0'.repeat(40)}`;
  const balanceDisplay = balance != null ? balance : 'NaN';

  // Pass network (chain) to formatAddress for proper formatting
  const formattedAddress = formatAddress(
    address || fallbackAddress,
    4,
    network,
  );

  const handleCopyBtnClick = () => {
    copy();
  };

  const showTooltipWithTimeout = () => {
    setShowTooltip(true);
    // Auto-hide tooltip after 2 seconds on mobile
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    const timeout = setTimeout(() => setShowTooltip(false), 2000);
    setTooltipTimeout(timeout);
  };

  const hideTooltip = () => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    setShowTooltip(false);
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault(); // Prevent mouse events from firing
    if (showTooltip) {
      hideTooltip();
    } else {
      showTooltipWithTimeout();
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
    };
  }, [tooltipTimeout]);

  const handleViewBtnClick = () => {
    if (!currentChain || !address) {
      return;
    }

    try {
      // Ensure currentChain is a valid SupportedChain
      const chain = currentChain as 'bnb' | 'solana';
      const chainConfig = getChainConfig(chain);
      const explorerUrl = chainConfig.explorerUrl;

      if (!explorerUrl) {
        console.warn(`No explorer URL found for chain: ${chain}`);
        return;
      }

      // Format URL based on chain
      // Both BNB and Solana use the same format: /address/{address}
      const url = `${explorerUrl}/address/${address}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening explorer:', error);
    }
  };

  return (
    <Popover trigger="hover" isLazy>
      <PopoverTrigger>
        <Button height="48px" fontSize={{ base: '0.625rem', md: '1rem' }}>
          {formattedAddress}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverHeader>
          <Flex justify="space-between" align="center" position="relative">
            <Text>Thông tin ví</Text>
            <Flex position="relative">
              {showTooltip && (
                <Box
                  position="absolute"
                  right="100%"
                  top="50%"
                  transform="translateY(-50%)"
                  bg="black"
                  color="white"
                  px={3}
                  py={2}
                  borderRadius="md"
                  fontSize="sm"
                  whiteSpace="nowrap"
                  mr={2}
                  zIndex={9999}
                  _before={{
                    content: '""',
                    position: 'absolute',
                    left: '100%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderLeft: '6px solid black',
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent',
                  }}
                >
                  {isCopied ? 'Đã copy!' : 'Copy địa chỉ ví'}
                </Box>
              )}
              <IconButton
                aria-label="Copy wallet address"
                icon={isCopied ? <CheckIcon /> : <CopyIcon />}
                size="sm"
                variant="ghost"
                onClick={handleCopyBtnClick}
                isDisabled={isCopied}
                colorScheme={isCopied ? 'green' : 'gray'}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                style={{ touchAction: 'manipulation' }}
              />
            </Flex>
          </Flex>
        </PopoverHeader>
        <PopoverBody>
          <Stat>
            <StatLabel>{network || '-'}</StatLabel>
            <StatNumber>
              {balanceDisplay} {symbol || '-'}
            </StatNumber>
          </Stat>
          <VisuallyHidden ref={ref}>
            {address || fallbackAddress}
          </VisuallyHidden>
        </PopoverBody>
        <PopoverFooter d="flex" justifyContent="flex-end">
          <ButtonGroup size="sm">
            <Button colorScheme="cyan" onClick={handleViewBtnClick}>
              Xem ví trên scan
            </Button>
            <Button colorScheme="red" onClick={() => signOut()}>
              Ngắt kết nối
            </Button>
          </ButtonGroup>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

export default WalletInfo;
